import { useCallback } from "react";

import { setMenuItemStopList, updateMenuCategory } from "services/menu/rest";
import { MenuCategoryDto } from "services/menu/restModels";

import { UseNotificationSnackbarReturn } from "shared/ui";

export interface MutationMessages {
    // Успех озвучиваем не всегда: в диалоге о результате говорит сама строка,
    // и тост на каждое нажатие только мешал бы убирать позиции подряд.
    success?: string;
    error: string;
}

export interface StopListMutations {
    setItemInStopList: (
        menuItemId: number,
        isInStopList: boolean,
        messages: MutationMessages
    ) => Promise<boolean>;
    setCategoryInStopList: (
        category: MenuCategoryDto,
        isInStopList: boolean,
        messages: MutationMessages
    ) => Promise<boolean>;
}

/*
* Мутации стоп-листа одной обёрткой: разбор ошибки и тост живут здесь, а вызывающий решает
* только, что откатить. Общего флага saving тут нет намеренно — обе мутации оптимистичные,
* а единый флаг глушил бы все кнопки списка разом (на 200 строках это заметно).
* */
export const useStopListMutations = (
    notify: UseNotificationSnackbarReturn
): StopListMutations => {
    const run = useCallback(
        async (action: () => Promise<unknown>, messages: MutationMessages): Promise<boolean> => {
            try {
                await action();
                if (messages.success) notify.showSuccess(messages.success);
                return true;
            } catch (error) {
                notify.showError(messages.error, error);
                return false;
            }
        },
        [notify]
    );

    const setItemInStopList = useCallback(
        (menuItemId: number, isInStopList: boolean, messages: MutationMessages) =>
            run(() => setMenuItemStopList(menuItemId, { isInStopList }), messages),
        [run]
    );

    // Ручка категории принимает тело целиком, поэтому недостающие поля берём из DTO списка.
    const setCategoryInStopList = useCallback(
        (category: MenuCategoryDto, isInStopList: boolean, messages: MutationMessages) =>
            run(
                () =>
                    updateMenuCategory(category.id, {
                        name: category.name,
                        schedule: category.schedule,
                        cookingTime: category.cookingTime,
                        isInStopList,
                    }),
                messages
            ),
        [run]
    );

    return { setItemInStopList, setCategoryInStopList };
};
